<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

// Plays or downloads a call recording or a caller's message. The audio comes
// through a five-minute signed link that Relay issues only to people allowed
// to hear it (and records); nothing is fetched until someone presses Play.
const props = defineProps({
  // (download: boolean) => Promise<{ url }>
  fetchLink: { type: Function, required: true },
  label: { type: String, default: '' },
});
const { t } = useI18n();

const src = ref('');
const loading = ref(false);
const failed = ref('');

const linkFor = async download => {
  loading.value = true;
  failed.value = '';
  try {
    return (await props.fetchLink(download)).url;
  } catch (error) {
    failed.value =
      error?.response?.status === 404
        ? t('RELAY_VOICE.RECORDING.GONE')
        : t('RELAY_VOICE.RECORDING.ERROR');
    return null;
  } finally {
    loading.value = false;
  }
};

const play = async () => {
  const url = await linkFor(false);
  if (url) src.value = url;
};

const download = async () => {
  const url = await linkFor(true);
  if (url) window.location.assign(url);
};
</script>

<template>
  <div class="flex flex-col gap-2" data-test-id="recording-player">
    <div class="flex items-center gap-2 flex-wrap">
      <span
        class="i-ph-waveform-bold size-4 text-n-slate-10 shrink-0"
        aria-hidden="true"
      />
      <span class="text-sm text-n-slate-12 flex-1 min-w-0 truncate">
        {{ label || t('RELAY_VOICE.RECORDING.CALL') }}
      </span>
      <Button
        v-if="!src"
        :label="t('RELAY_VOICE.RECORDING.PLAY')"
        icon="i-ph-play-fill"
        size="xs"
        variant="faded"
        :is-loading="loading"
        data-test-id="recording-play"
        @click="play"
      />
      <Button
        icon="i-ph-download-simple-bold"
        size="xs"
        variant="ghost"
        color="slate"
        :aria-label="t('RELAY_VOICE.RECORDING.DOWNLOAD')"
        :disabled="loading"
        @click="download"
      />
    </div>
    <audio
      v-if="src"
      :src="src"
      controls
      autoplay
      preload="none"
      class="w-full h-9"
      :aria-label="label || t('RELAY_VOICE.RECORDING.CALL')"
    />
    <p v-if="failed" role="alert" class="text-xs text-n-ruby-11">
      {{ failed }}
    </p>
    <p class="text-[0.6875rem] text-n-slate-10">
      {{ t('RELAY_VOICE.RECORDING.PRIVATE') }}
    </p>
  </div>
</template>
